import React, { useState, useEffect, useMemo } from 'react';
import { Card, Form, Switch, Button, message, Space, Typography, Alert, Table, Tag, Popconfirm, Input, Modal, Tabs } from 'antd';
import { SettingOutlined, SaveOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

const { Title, Text } = Typography;

interface SystemParameter {
    id: number;
    name: string;
    value: any;
    rawValue: string | null;
    description: string | null;
    createdAt: string;
    updatedAt: string;
}

const SystemParametersPage: React.FC = () => {
    const [parameters, setParameters] = useState<SystemParameter[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingParameter, setEditingParameter] = useState<SystemParameter | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchParameters();
    }, []);

    const fetchParameters = async () => {
        try {
            setLoading(true);
            const response = await apiService.get<ApiResponse<SystemParameter[]>>(
                API_ENDPOINTS.SYSTEM_PARAMETERS?.BASE || '/system-parameters'
            );
            setParameters(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (error) {
            logger.error('Error fetching system parameters:', error);
            message.error('Ошибка при загрузке параметров платформы');
            setParameters([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (parameter: SystemParameter) => {
        setEditingParameter(parameter);
        
        // Преобразуем значение для отображения в форме
        let displayValue: string = '';
        if (typeof parameter.value === 'boolean') {
            displayValue = parameter.value ? 'true' : 'false';
        } else if (typeof parameter.value === 'object' && parameter.value !== null) {
            displayValue = JSON.stringify(parameter.value, null, 2);
        } else if (parameter.value !== null && parameter.value !== undefined) {
            displayValue = String(parameter.value);
        }
        
        form.setFieldsValue({
            name: parameter.name,
            value: displayValue,
            description: parameter.description || ''
        });
        setIsModalVisible(true);
    };

    const handleSave = async (values: any) => {
        try {
            setLoading(true);
            const { name, value, description } = values;

            // Преобразуем значение в нужный тип
            let processedValue: any = value;
            
            // Если это строка 'true' или 'false', преобразуем в boolean
            if (typeof value === 'string' && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false')) {
                processedValue = value.toLowerCase() === 'true';
            }
            // Если это число
            else if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
                processedValue = parseFloat(value);
                if (Number.isInteger(processedValue)) {
                    processedValue = parseInt(value, 10);
                }
            }
            // Если это JSON (начинается с { или [)
            else if (typeof value === 'string' && (value.trim().startsWith('{') || value.trim().startsWith('['))) {
                try {
                    processedValue = JSON.parse(value);
                } catch (e) {
                    // Если не удалось распарсить, оставляем как строку
                    processedValue = value;
                }
            }

            if (editingParameter) {
                await apiService.put(
                    `${API_ENDPOINTS.SYSTEM_PARAMETERS?.BASE || '/system-parameters'}/${editingParameter.name}`,
                    { value: processedValue, description }
                );
                message.success('Параметр успешно обновлен');
            } else {
                await apiService.post(
                    `${API_ENDPOINTS.SYSTEM_PARAMETERS?.BASE || '/system-parameters'}/${name}`,
                    { value: processedValue, description }
                );
                message.success('Параметр успешно создан');
            }

            setIsModalVisible(false);
            setEditingParameter(null);
            form.resetFields();
            await fetchParameters();
        } catch (error: any) {
            logger.error('Error saving parameter:', error);
            message.error(error.response?.data?.message || 'Ошибка при сохранении параметра');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (name: string) => {
        try {
            await apiService.delete(
                `${API_ENDPOINTS.SYSTEM_PARAMETERS?.BASE || '/system-parameters'}/${name}`
            );
            message.success('Параметр успешно удален');
            await fetchParameters();
        } catch (error: any) {
            logger.error('Error deleting parameter:', error);
            message.error(error.response?.data?.message || 'Ошибка при удалении параметра');
        }
    };

    const handleToggleSubscriptions = async (enabled: boolean) => {
        try {
            setLoading(true);
            await apiService.put(
                `${API_ENDPOINTS.SYSTEM_PARAMETERS?.BASE || '/system-parameters'}/subscriptions/enabled`,
                { enabled }
            );
            message.success(`Подписки ${enabled ? 'включены' : 'выключены'}`);
            await fetchParameters();
        } catch (error: any) {
            logger.error('Error toggling subscriptions:', error);
            message.error(error.response?.data?.message || 'Ошибка при изменении параметра');
        } finally {
            setLoading(false);
        }
    };

    const getEnableSubscriptionsParameter = (): SystemParameter | undefined => {
        return parameters.find(p => p.name === 'enable_subscriptions');
    };

    // Получаем значение параметра enable_subscriptions (по умолчанию true, если не загружен)
    const enableSubscriptionsParam = getEnableSubscriptionsParameter();
    let enableSubscriptionsValue = true; // По умолчанию включено
    
    if (enableSubscriptionsParam) {
        // Если параметр загружен, используем его значение
        if (typeof enableSubscriptionsParam.value === 'boolean') {
            enableSubscriptionsValue = enableSubscriptionsParam.value;
        } else if (typeof enableSubscriptionsParam.value === 'string') {
            // Если это строка, пытаемся преобразовать в boolean
            enableSubscriptionsValue = enableSubscriptionsParam.value.toLowerCase() === 'true';
        } else if (enableSubscriptionsParam.rawValue) {
            // Если value не распарсен, используем rawValue
            enableSubscriptionsValue = enableSubscriptionsParam.rawValue.toLowerCase() === 'true';
        }
    }

    // Группируем параметры по категориям
    const groupedParameters = useMemo(() => {
        const groups: { [key: string]: SystemParameter[] } = {
            'Локализация': [],
            'Email': [],
            'Подписки': [],
            'Файлы': [],
            'Турниры': [],
            'Категории': [],
            'Матчи': [],
            'Пагинация': [],
            'Rate Limiting': [],
            'Аутентификация': [],
            'Кэш': [],
            'Функциональные флаги': [],
            'Другие': []
        };

        const categoryKeywords: { [key: string]: string[] } = {
            'Локализация': ['english_language', 'language'],
            'Email': ['admin_email', 'email'],
            'Подписки': ['subscription', 'extended_license'],
            'Файлы': ['file', 'avatar', 'upload', 'allowed'],
            'Турниры': ['tournament'],
            'Категории': ['age', 'weight'],
            'Матчи': ['match', 'tatami', 'round', 'notes'],
            'Пагинация': ['page', 'limit', 'pagination'],
            'Rate Limiting': ['rate', 'attempts', 'window', 'requests', 'auth_max', 'register_max', 'api_max', 'strict', 'moderate'],
            'Аутентификация': ['token', 'password', 'name_length', 'expiration'],
            'Кэш': ['cache', 'ttl'],
            'Функциональные флаги': ['enable_']
        };

        parameters.forEach(param => {
            const paramName = param.name.toLowerCase();
            let categorized = false;

            for (const [category, keywords] of Object.entries(categoryKeywords)) {
                if (keywords.some(keyword => paramName.includes(keyword.toLowerCase()))) {
                    groups[category].push(param);
                    categorized = true;
                    break;
                }
            }

            if (!categorized) {
                groups['Другие'].push(param);
            }
        });

        // Удаляем пустые группы
        Object.keys(groups).forEach(key => {
            if (groups[key].length === 0) {
                delete groups[key];
            }
        });

        return groups;
    }, [parameters]);

    const columns: ColumnsType<SystemParameter> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <Text strong>{name}</Text>,
            width: '25%',
        },
        {
            title: 'Значение',
            dataIndex: 'value',
            key: 'value',
            render: (value: any) => {
                if (typeof value === 'boolean') {
                    return <Tag color={value ? 'green' : 'red'}>{value ? 'Включено' : 'Выключено'}</Tag>;
                }
                if (typeof value === 'number') {
                    return <Text>{value}</Text>;
                }
                if (typeof value === 'object' && value !== null) {
                    return <Text code style={{ whiteSpace: 'pre-wrap', maxWidth: '400px', display: 'block' }}>
                        {JSON.stringify(value, null, 2)}
                    </Text>;
                }
                return <Text style={{ maxWidth: '400px', display: 'block', wordBreak: 'break-word' }}>
                    {String(value || 'null')}
                </Text>;
            },
            width: '30%',
        },
        {
            title: 'Описание',
            dataIndex: 'description',
            key: 'description',
            render: (description: string | null) => description || <Text type="secondary">Нет описания</Text>,
            width: '30%',
        },
        {
            title: 'Действия',
            key: 'actions',
            width: '15%',
            render: (_: unknown, record: SystemParameter) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        Редактировать
                    </Button>
                    {!['enable_subscriptions', 'extended_license_price'].includes(record.name) && (
                        <Popconfirm
                            title="Вы уверены, что хотите удалить этот параметр?"
                            onConfirm={() => handleDelete(record.name)}
                            okText="Да"
                            cancelText="Нет"
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            >
                                Удалить
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={2}>
                <SettingOutlined style={{ marginRight: 8 }} />
                Параметры платформы
            </Title>

            <Alert
                message="Управление параметрами системы"
                description="Параметры платформы позволяют настраивать различные функции системы. Значения могут быть разных типов: boolean, число, строка или JSON объект."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            {/* Специальная секция для параметра включения подписок */}
            <Card
                title="Включить подписки"
                style={{ marginBottom: 24 }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                        При включенных подписках система будет проверять лимиты подписок при создании турниров.
                        Без подписки клубы могут создать только один турнир.
                    </Text>
                    <Space align="center" style={{ width: '100%', marginTop: 16 }}>
                        <Text strong>Статус:</Text>
                        <Switch
                            checked={enableSubscriptionsValue}
                            onChange={handleToggleSubscriptions}
                            checkedChildren="Включено"
                            unCheckedChildren="Выключено"
                            loading={loading}
                        />
                    </Space>
                </Space>
            </Card>

            {/* Стоимость расширенной лицензии */}
            <Card title="Стоимость расширенной лицензии" style={{ marginBottom: 24 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                        Сумма (в рублях), которую клуб оплачивает один раз для получения права создавать турниры.
                        При значении 0 оплата не требуется.
                    </Text>
                    {(() => {
                        const extPriceParam = parameters.find(p => p.name === 'extended_license_price');
                        const displayValue = extPriceParam != null
                            ? (typeof extPriceParam.value === 'number' ? String(extPriceParam.value) : String(extPriceParam.value ?? ''))
                            : '—';
                        return (
                            <Space align="center" style={{ width: '100%', marginTop: 16 }}>
                                <Text strong>Стоимость (₽):</Text>
                                <Text>{displayValue}</Text>
                                {extPriceParam && (
                                    <Button type="link" size="small" onClick={() => handleEdit(extPriceParam)}>
                                        Изменить
                                    </Button>
                                )}
                            </Space>
                        );
                    })()}
                </Space>
            </Card>

            {/* Таблица всех параметров с группировкой по категориям */}
            <Card title="Все параметры" loading={loading}>
                <Tabs
                    defaultActiveKey={Object.keys(groupedParameters)[0]}
                    items={Object.entries(groupedParameters).map(([category, params]) => ({
                        key: category,
                        label: `${category} (${params.length})`,
                        children: (
                            <Table
                                columns={columns}
                                dataSource={params}
                                rowKey="id"
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) => `Параметров в категории: ${total}`,
                                }}
                                size="middle"
                            />
                        )
                    }))}
                    type="card"
                />
            </Card>

            {/* Модальное окно для редактирования/создания параметра */}
            <Modal
                title={editingParameter ? 'Редактировать параметр' : 'Создать параметр'}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingParameter(null);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                >
                    <Form.Item
                        name="name"
                        label="Название параметра"
                        rules={[{ required: true, message: 'Пожалуйста, введите название параметра' }]}
                    >
                        <Input
                            placeholder="например: enable_subscriptions"
                            disabled={!!editingParameter}
                        />
                    </Form.Item>

                    <Form.Item
                        name="value"
                        label="Значение"
                        rules={[{ required: true, message: 'Пожалуйста, введите значение' }]}
                        tooltip="Может быть boolean (true/false), число, строка или JSON объект"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder='true, false, число, строка или JSON: {"key": "value"}'
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Описание"
                    >
                        <Input.TextArea
                            rows={2}
                            placeholder="Описание параметра (необязательно)"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button
                                onClick={() => {
                                    setIsModalVisible(false);
                                    setEditingParameter(null);
                                    form.resetFields();
                                }}
                            >
                                Отмена
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={loading}
                            >
                                Сохранить
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SystemParametersPage;
